import {
  type ApiOutputs,
  type ReferralExample,
  type ReferralTemplateDraft,
  referralTemplateListSchema,
} from "@rosc/contracts";
import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import { referenceClient } from "@/lib/orpc/client";
import { queryClient } from "@/lib/query-client";
import {
  getVisitScope,
  useVisitScope,
  type VisitScope,
} from "@/lib/visit-queries";

export type ReferralTemplate =
  ApiOutputs["listReferralTemplates"]["items"][number];
export type ReferralBuiltIn =
  ApiOutputs["listReferralTemplates"]["builtIns"][number];
export type ReferralExtraction = ApiOutputs["createReferralTemplateExtraction"];
export type ReferralTemplateInput = ReferralTemplateDraft & {
  isDefault?: boolean;
  source?: "manual" | "example" | "built-in";
};

// One choice in the referral dialog: a built-in (by key) or an own template.
export type ReferralChoice = {
  id: string;
  name: string;
  sections: string[];
  specialty: string | null;
  builtIn: boolean;
  isDefault: boolean;
};

const key = (scope: VisitScope | null): readonly unknown[] => [
  "referral-templates",
  scope?.uid,
  scope?.accountPublicId,
  scope?.generation,
];

const context = (scope: VisitScope | null) => ({
  expectedUserUid: scope?.uid,
});

export function useReferralTemplates({ enabled = true } = {}) {
  const scope = useVisitScope();
  const query = useQuery({
    queryKey: key(scope),
    queryFn: async ({ signal }) =>
      referralTemplateListSchema.parse(
        await referenceClient.listReferralTemplates(
          { query: {} },
          { context: context(scope), signal },
        ),
      ),
    enabled: enabled && !!scope,
    staleTime: 30_000,
    retry: false,
  });
  const refresh = useCallback(
    () => queryClient.invalidateQueries({ queryKey: key(scope) }),
    [scope],
  );
  return {
    items: query.data?.items ?? [],
    builtIns: query.data?.builtIns ?? [],
    loading: !!scope && enabled && query.isPending,
    error: query.isError,
    ready: !!query.data,
    refresh,
  };
}

const refreshList = () =>
  queryClient.invalidateQueries({ queryKey: key(getVisitScope()) });

export const createReferralTemplate = async (
  body: ReferralTemplateInput | { builtInKey: ReferralBuiltIn["key"] },
) => {
  const scope = getVisitScope();
  const created = await referenceClient.createReferralTemplate(
    { query: {}, body: body as never },
    { context: context(scope) },
  );
  await refreshList();
  return created;
};

export const updateReferralTemplate = async (
  publicId: string,
  body: Partial<ReferralTemplateInput>,
) => {
  const scope = getVisitScope();
  const { source: _source, ...edit } = body;
  const updated = await referenceClient.updateReferralTemplate(
    { params: { public_id: publicId }, query: {}, body: edit },
    { context: context(scope) },
  );
  await refreshList();
  return updated;
};

export const deleteReferralTemplate = async (publicId: string) => {
  const scope = getVisitScope();
  await referenceClient.deleteReferralTemplate(
    { params: { public_id: publicId }, query: {} },
    { context: context(scope) },
  );
  await refreshList();
};

export const extractReferralTemplate = (
  example: ReferralExample,
  signal?: AbortSignal,
) =>
  referenceClient.createReferralTemplateExtraction(
    { query: {}, body: { example } },
    { context: context(getVisitScope()), signal },
  );

/** Built-ins first, then the clinician's own templates (default first). */
export const referralChoices = (
  builtIns: readonly ReferralBuiltIn[],
  items: readonly ReferralTemplate[],
  builtInName: (key: ReferralBuiltIn["key"]) => string,
): ReferralChoice[] => [
  ...[...items]
    .sort((a, b) => Number(b.isDefault) - Number(a.isDefault))
    .map((item) => ({
      id: item.public_id,
      name: item.name,
      sections: item.sections,
      specialty: item.specialty,
      builtIn: false,
      isDefault: item.isDefault,
    })),
  ...builtIns.map((item) => ({
    id: item.key,
    name: builtInName(item.key),
    sections: item.sections,
    specialty: null,
    builtIn: true,
    isDefault: false,
  })),
];

/**
 * The template the dialog starts on: the one used last (if it still exists),
 * else the clinician's default, else the first built-in.
 */
export const initialReferralChoice = (
  choices: readonly ReferralChoice[],
  lastUsed: string | undefined,
): string | undefined =>
  choices.find((choice) => choice.id === lastUsed)?.id ??
  choices.find((choice) => choice.isDefault)?.id ??
  choices.find((choice) => choice.builtIn)?.id ??
  choices[0]?.id;

// Read a file the clinician picked as base64 (no data: prefix).
export const fileBase64 = async (file: Blob): Promise<string> => {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = "";
  for (let index = 0; index < bytes.length; index += 0x8000)
    binary += String.fromCharCode(...bytes.subarray(index, index + 0x8000));
  return btoa(binary);
};
